import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play,
  Square,
  RotateCcw,
  Radio,
  X,
  Check,
  Users,
  Highlighter,
  Sparkles,
  Key,
  Volume2,
  ChevronDown,
  ChevronUp,
  Music,
  Sliders,
  Activity,
  Zap,
  Headphones,
  Settings,
  Flame,
} from 'lucide-react';
import {
  GoogleGenAIConfig,
  loadAIConfig,
  saveAIConfig,
  CHARACTER_VOICE_PRESETS,
  CharacterVoicePreset,
  getNarrationController,
  ELEVENLABS_CHARACTER_STATES,
} from '../../services/googleGenAI';
import {
  CHARACTER_ACOUSTIC_DOSSIERS,
  CharacterAcousticDossier,
} from '../../data/characterAcousticDossiers';

export interface VoiceStudioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfigChange?: (newConfig: GoogleGenAIConfig) => void;
}

export type VoiceEngineTab =
  | 'dsp-master'
  | 'gemini-live'
  | 'google-cloud'
  | 'kokoro'
  | 'elevenlabs'
  | 'dossier-inspector';

export type VisualizerMode = 'spectrogram' | 'oscilloscope';

export const VoiceStudioModal: React.FC<VoiceStudioModalProps> = ({
  isOpen,
  onClose,
  onConfigChange,
}) => {
  const [config, setConfig] = useState<GoogleGenAIConfig>(loadAIConfig);
  const [selectedPresetId, setSelectedPresetId] = useState<string>(
    config.characterPreset || 'marcus_ward'
  );
  const [activeTab, setActiveTab] = useState<VoiceEngineTab>('dsp-master');
  const [visualizerMode, setVisualizerMode] = useState<VisualizerMode>('spectrogram');
  const [isDSPMastered, setIsDSPMastered] = useState<boolean>(true); // A/B Switch: true = Cinema Master, false = Raw Direct
  const [isTesting, setIsTesting] = useState(false);
  const [testText, setTestText] = useState<string>(
    CHARACTER_VOICE_PRESETS[config.characterPreset || 'marcus_ward']?.testPhrase ||
      "Forty cycles on the Geryon keel. You don't jump through space, kid... you let the ledger unthread you."
  );

  // Audio Playback & Real-Time DSP State
  const [playingFile, setPlayingFile] = useState<string | null>(null);
  const [sampleProgress, setSampleProgress] = useState<number>(0);
  const [sampleDuration, setSampleDuration] = useState<number>(0);
  const [sampleCurrentTime, setSampleCurrentTime] = useState<number>(0);
  const [isJukeboxOpen, setIsJukeboxOpen] = useState(false);
  const [vuLeft, setVuLeft] = useState<number>(-40);
  const [vuRight, setVuRight] = useState<number>(-40);
  const [peakHoldLeft, setPeakHoldLeft] = useState<number>(-40);
  const [isClipping, setIsClipping] = useState<boolean>(false);

  // Hardware Audio Graph References
  const audioCtxRef = useRef<AudioContext | null>(null);
  const activeSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dspGainRef = useRef<GainNode | null>(null);
  const rawGainRef = useRef<GainNode | null>(null);
  const lowShelfRef = useRef<BiquadFilterNode | null>(null);
  const presenceRef = useRef<BiquadFilterNode | null>(null);
  const airShelfRef = useRef<BiquadFilterNode | null>(null);
  const shaperRef = useRef<WaveShaperNode | null>(null);
  const cabinDelayRef = useRef<DelayNode | null>(null);
  const delayGainRef = useRef<GainNode | null>(null);
  const commFilterRef = useRef<BiquadFilterNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bufferCacheRef = useRef<Map<string, AudioBuffer>>(new Map());
  const playbackStartTimeRef = useRef<number>(0);
  const durationRef = useRef<number>(0);

  // Non-linear transfer curve for authentic analog comms tube saturation
  const makeDistortionCurve = useCallback((amount: number): Float32Array => {
    const k = Math.max(1, amount);
    const n_samples = 22050;
    const curve = new Float32Array(n_samples);
    const deg = Math.PI / 180;
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  }, []);

  // Initializes the Hardware-Grade Web Audio Cinema DSP Rack
  const initAudioEngine = useCallback(() => {
    if (audioCtxRef.current) {
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
      return audioCtxRef.current;
    }

    const AudioContextClass =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioContextClass();
    audioCtxRef.current = ctx;

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.85;
    analyserRef.current = analyser;

    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(1.0, ctx.currentTime);

    // Branch 1: DSP Mastered Path
    const dspGain = ctx.createGain();
    dspGain.gain.setValueAtTime(isDSPMastered ? 1.0 : 0.0, ctx.currentTime);
    dspGainRef.current = dspGain;

    const lowShelf = ctx.createBiquadFilter();
    lowShelf.type = 'lowshelf';
    lowShelf.frequency.setValueAtTime(110, ctx.currentTime);
    lowShelf.gain.setValueAtTime(Math.min(6.0, config.chestResonance * 5.0), ctx.currentTime);
    lowShelfRef.current = lowShelf;

    const presence = ctx.createBiquadFilter();
    presence.type = 'peaking';
    presence.frequency.setValueAtTime(3200, ctx.currentTime);
    presence.gain.setValueAtTime(3.2, ctx.currentTime);
    presence.Q.setValueAtTime(0.85, ctx.currentTime);
    presenceRef.current = presence;

    const airShelf = ctx.createBiquadFilter();
    airShelf.type = 'highshelf';
    airShelf.frequency.setValueAtTime(9500, ctx.currentTime);
    airShelf.gain.setValueAtTime(2.2, ctx.currentTime);
    airShelfRef.current = airShelf;

    const shaper = ctx.createWaveShaper();
    shaper.curve = makeDistortionCurve(Math.max(1, config.commsGrit * 5.0));
    shaper.oversample = '2x';
    shaperRef.current = shaper;

    const comp = ctx.createDynamicsCompressor();
    comp.threshold.setValueAtTime(-18, ctx.currentTime);
    comp.knee.setValueAtTime(12, ctx.currentTime);
    comp.ratio.setValueAtTime(3.2, ctx.currentTime);
    comp.attack.setValueAtTime(0.008, ctx.currentTime);
    comp.release.setValueAtTime(0.12, ctx.currentTime);

    // Early Bulkhead Reverb / Cabin Delay
    const cabinDelay = ctx.createDelay();
    cabinDelay.delayTime.setValueAtTime(0.038, ctx.currentTime);
    cabinDelayRef.current = cabinDelay;

    const delayGain = ctx.createGain();
    delayGain.gain.setValueAtTime(Math.min(0.35, config.cabinReverb * 0.35), ctx.currentTime);
    delayGainRef.current = delayGain;

    const delayFilter = ctx.createBiquadFilter();
    delayFilter.type = 'lowpass';
    delayFilter.frequency.setValueAtTime(3400, ctx.currentTime);

    // Optional Bandpass for Comm-Link Transceiver Mode
    const commFilter = ctx.createBiquadFilter();
    commFilter.type = config.masteringProfile === 'comm_link' ? 'bandpass' : 'allpass';
    commFilter.frequency.setValueAtTime(config.helmetBandwidth || 1800, ctx.currentTime);
    commFilter.Q.setValueAtTime(config.masteringProfile === 'comm_link' ? 1.4 : 0.7, ctx.currentTime);
    commFilterRef.current = commFilter;

    // Assemble DSP Chain:
    // dspInput -> lowShelf -> presence -> airShelf -> shaper -> comp -> commFilter -> dspGain
    lowShelf.connect(presence);
    presence.connect(airShelf);
    airShelf.connect(shaper);
    shaper.connect(comp);
    comp.connect(commFilter);
    commFilter.connect(dspGain);

    // Parallel Bulkhead Ambience: comp -> cabinDelay -> delayFilter -> delayGain -> dspGain
    comp.connect(cabinDelay);
    cabinDelay.connect(delayFilter);
    delayFilter.connect(delayGain);
    delayGain.connect(dspGain);

    // Branch 2: Raw / Unprocessed Direct Path
    const rawGain = ctx.createGain();
    rawGain.gain.setValueAtTime(isDSPMastered ? 0.0 : 1.0, ctx.currentTime);
    rawGainRef.current = rawGain;

    // Both paths converge into Analyser -> Master Gain -> Output
    dspGain.connect(analyser);
    rawGain.connect(analyser);
    analyser.connect(masterGain);
    masterGain.connect(ctx.destination);

    return ctx;
  }, [config, isDSPMastered, makeDistortionCurve]);

  // Live A/B DSP Toggle Smooth Crossfade
  const toggleABCompare = () => {
    const nextState = !isDSPMastered;
    setIsDSPMastered(nextState);

    const ctx = audioCtxRef.current;
    if (ctx && dspGainRef.current && rawGainRef.current) {
      const now = ctx.currentTime;
      dspGainRef.current.gain.cancelScheduledValues(now);
      rawGainRef.current.gain.cancelScheduledValues(now);

      dspGainRef.current.gain.setValueAtTime(dspGainRef.current.gain.value, now);
      rawGainRef.current.gain.setValueAtTime(rawGainRef.current.gain.value, now);

      if (nextState) {
        // Switch to PRO MASTERED CINEMA DSP
        dspGainRef.current.gain.linearRampToValueAtTime(1.0, now + 0.04);
        rawGainRef.current.gain.linearRampToValueAtTime(0.0, now + 0.04);
      } else {
        // Switch to RAW DIRECT
        dspGainRef.current.gain.linearRampToValueAtTime(0.0, now + 0.04);
        rawGainRef.current.gain.linearRampToValueAtTime(1.0, now + 0.04);
      }
    }
  };

  // Updates Real-Time DSP Parameters
  useEffect(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;

    if (lowShelfRef.current) {
      lowShelfRef.current.gain.setValueAtTime(Math.min(6.0, config.chestResonance * 5.0), now);
    }
    if (shaperRef.current) {
      shaperRef.current.curve = makeDistortionCurve(Math.max(1, config.commsGrit * 5.0));
    }
    if (delayGainRef.current) {
      delayGainRef.current.gain.setValueAtTime(Math.min(0.35, config.cabinReverb * 0.35), now);
    }
    if (commFilterRef.current) {
      commFilterRef.current.type = config.masteringProfile === 'comm_link' ? 'bandpass' : 'allpass';
      commFilterRef.current.frequency.setValueAtTime(config.helmetBandwidth || 1800, now);
      commFilterRef.current.Q.setValueAtTime(config.masteringProfile === 'comm_link' ? 1.4 : 0.7, now);
    }
  }, [config, makeDistortionCurve]);

  // Stops active sample playback
  const stopSampleAudio = useCallback(() => {
    if (activeSourceRef.current) {
      try {
        activeSourceRef.current.stop();
        activeSourceRef.current.disconnect();
      } catch {
        // ignore already stopped node
      }
      activeSourceRef.current = null;
    }
    setPlayingFile(null);
    setSampleProgress(0);
    setSampleCurrentTime(0);
    setVuLeft(-40);
    setVuRight(-40);
    setIsClipping(false);
  }, []);

  // Plays an audio file routed directly through the Cinema DSP Mastering Rack
  const playSampleAudio = async (targetFile?: string) => {
    if (!targetFile) return;
    if (playingFile === targetFile) {
      stopSampleAudio();
      return;
    }
    stopSampleAudio();

    const ctx = initAudioEngine();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    setPlayingFile(targetFile);

    // 1. Check in-memory buffer cache
    let buffer = bufferCacheRef.current.get(targetFile);
    if (!buffer) {
      const primaryUrl = `/audio/samples/elevenlabs/${targetFile}`;
      const fallbackUrl = `/audio/samples/${targetFile}`;

      try {
        let res = await fetch(primaryUrl);
        if (!res.ok) {
          res = await fetch(fallbackUrl);
        }
        if (!res.ok) {
          throw new Error(`Failed to load audio sample from ${primaryUrl} or ${fallbackUrl}`);
        }
        const arrayBuf = await res.arrayBuffer();
        buffer = await ctx.decodeAudioData(arrayBuf);
        bufferCacheRef.current.set(targetFile, buffer);
      } catch (err) {
        console.warn('Audio decoding failed for', targetFile, err);
        setPlayingFile(null);
        return;
      }
    }

    // 2. Play initial PTT squelch click if in Comm-Link mode
    if (config.masteringProfile === 'comm_link' && config.squelchClicks && lowShelfRef.current) {
      try {
        const t = ctx.currentTime;
        const squelchOsc = ctx.createOscillator();
        const squelchGain = ctx.createGain();
        squelchOsc.frequency.setValueAtTime(1400, t);
        squelchOsc.frequency.exponentialRampToValueAtTime(380, t + 0.035);
        squelchGain.gain.setValueAtTime(0.001, t);
        squelchGain.gain.linearRampToValueAtTime(0.2, t + 0.006);
        squelchGain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
        squelchOsc.connect(squelchGain);
        squelchGain.connect(lowShelfRef.current);
        squelchOsc.start(t);
        squelchOsc.stop(t + 0.045);
      } catch {
        // squelch fallback
      }
    }

    // 3. Create audio buffer source node and connect to both DSP and Raw paths
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    activeSourceRef.current = source;

    if (lowShelfRef.current && rawGainRef.current) {
      source.connect(lowShelfRef.current); // Enters DSP mastering chain
      source.connect(rawGainRef.current); // Enters Raw bypass chain
    }

    const dur = buffer.duration;
    durationRef.current = dur;
    setSampleDuration(dur);
    playbackStartTimeRef.current = ctx.currentTime;

    source.onended = () => {
      if (activeSourceRef.current === source) {
        setPlayingFile(null);
        setSampleProgress(0);
        setSampleCurrentTime(0);
        activeSourceRef.current = null;
      }
    };

    source.start(0);
  };

  // High-Performance Real-Time Canvas Spectrogram / Oscilloscope Loop
  useEffect(() => {
    let active = true;

    const renderVisualizer = () => {
      if (!active) return;
      animFrameRef.current = requestAnimationFrame(renderVisualizer);

      const canvas = canvasRef.current;
      const analyser = analyserRef.current;
      if (!canvas || !analyser) return;

      const ctx2d = canvas.getContext('2d');
      if (!ctx2d) return;

      const width = canvas.width;
      const height = canvas.height;

      // Track playback progress
      if (audioCtxRef.current && activeSourceRef.current && durationRef.current > 0) {
        const elapsed = audioCtxRef.current.currentTime - playbackStartTimeRef.current;
        const clamped = Math.max(0, Math.min(durationRef.current, elapsed));
        setSampleCurrentTime(clamped);
        setSampleProgress((clamped / durationRef.current) * 100);
      }

      // Background CRT Grid
      ctx2d.fillStyle = 'rgba(3, 8, 16, 0.35)';
      ctx2d.fillRect(0, 0, width, height);

      // Subtle Phosphor Grid lines
      ctx2d.strokeStyle = 'rgba(0, 243, 255, 0.07)';
      ctx2d.lineWidth = 1;
      ctx2d.beginPath();
      for (let x = 0; x < width; x += 30) {
        ctx2d.moveTo(x, 0);
        ctx2d.lineTo(x, height);
      }
      for (let y = 0; y < height; y += 20) {
        ctx2d.moveTo(0, y);
        ctx2d.lineTo(width, y);
      }
      ctx2d.stroke();

      if (visualizerMode === 'spectrogram') {
        // --- MODE 1: FFT REAL-TIME SPECTRAL BARS ---
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        // Calculate RMS Level for VU Meters
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / bufferLength);
        const db = rms > 0 ? 20 * Math.log10(rms / 255) : -45;

        setVuLeft(db);
        setVuRight(db + (Math.random() * 1.5 - 0.75));
        setPeakHoldLeft((prev) => (db > prev ? db : Math.max(-40, prev - 0.25)));
        setIsClipping(db > -1.5);

        const barCount = 48;
        const barWidth = (width / barCount) - 2;

        for (let i = 0; i < barCount; i++) {
          // Logarithmic scale frequency mapping
          const binIndex = Math.floor(Math.pow(i / barCount, 1.45) * (bufferLength * 0.75));
          const val = dataArray[binIndex] || 0;
          const barHeight = (val / 255) * (height - 18);
          const x = i * (barWidth + 2);
          const y = height - barHeight - 4;

          // Gradient: Neon Cyan to Burnt Amber Overdrive
          const grad = ctx2d.createLinearGradient(0, height, 0, 0);
          if (isDSPMastered) {
            grad.addColorStop(0, '#00f3ff');
            grad.addColorStop(0.65, '#38bdf8');
            grad.addColorStop(0.9, '#f59e0b');
            grad.addColorStop(1, '#ef4444');
          } else {
            // Raw mode: flatter, colder gray/magenta tone
            grad.addColorStop(0, '#94a3b8');
            grad.addColorStop(0.7, '#cbd5e1');
            grad.addColorStop(1, '#ec4899');
          }

          ctx2d.fillStyle = grad;
          ctx2d.fillRect(x, y, barWidth, barHeight);

          // Peak Cap Needle
          if (val > 15) {
            ctx2d.fillStyle = isDSPMastered ? '#ffffff' : '#f43f5e';
            ctx2d.fillRect(x, Math.max(2, y - 2), barWidth, 1.5);
          }
        }
      } else {
        // --- MODE 2: OSCILLOSCOPE TIME-DOMAIN LASER BEAM ---
        const bufferLength = analyser.fftSize;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteTimeDomainData(dataArray);

        ctx2d.lineWidth = 2;
        ctx2d.strokeStyle = isDSPMastered ? '#00f3ff' : '#ec4899';
        ctx2d.shadowBlur = 8;
        ctx2d.shadowColor = isDSPMastered ? '#00f3ff' : '#ec4899';
        ctx2d.beginPath();

        const sliceWidth = (width * 1.0) / bufferLength;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          const v = dataArray[i] / 128.0;
          const y = (v * height) / 2;

          if (i === 0) {
            ctx2d.moveTo(x, y);
          } else {
            ctx2d.lineTo(x, y);
          }
          x += sliceWidth;
        }

        ctx2d.lineTo(width, height / 2);
        ctx2d.stroke();
        ctx2d.shadowBlur = 0; // reset
      }

      // Draw Center HUD Frequency Scale
      ctx2d.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx2d.font = '8px monospace';
      ctx2d.fillText('20 Hz', 8, 12);
      ctx2d.fillText('110 Hz [SUB]', width * 0.18, 12);
      ctx2d.fillText('1 kHz', width * 0.45, 12);
      ctx2d.fillText('3.2 kHz [PRESENCE]', width * 0.65, 12);
      ctx2d.fillText('18 kHz [AIR]', width - 65, 12);
    };

    renderVisualizer();

    return () => {
      active = false;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [isDSPMastered, visualizerMode]);

  useEffect(() => {
    return () => {
      stopSampleAudio();
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    };
  }, [stopSampleAudio]);

  useEffect(() => {
    if (isOpen) {
      const current = loadAIConfig();
      setConfig(current);
      setSelectedPresetId(current.characterPreset || 'marcus_ward');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentDossier: CharacterAcousticDossier | undefined =
    CHARACTER_ACOUSTIC_DOSSIERS[selectedPresetId] || CHARACTER_ACOUSTIC_DOSSIERS.marcus_ward;

  const handlePresetSelect = (preset: CharacterVoicePreset) => {
    const states = ELEVENLABS_CHARACTER_STATES[preset.id] || [];
    const firstState = states[0];
    const initialText =
      config.voiceModel === 'elevenlabs' && firstState
        ? firstState.script
        : preset.testPhrase;

    setSelectedPresetId(preset.id);
    setTestText(initialText);

    const dossier = CHARACTER_ACOUSTIC_DOSSIERS[preset.id];

    const updated: GoogleGenAIConfig = {
      ...config,
      characterPreset: preset.id,
      characterStateId: firstState?.stateId || config.characterStateId,
      voicePitch: dossier?.dspDefaults.voicePitch ?? preset.voicePitch,
      voicePace: dossier?.dspDefaults.voicePace ?? preset.voicePace,
      chestResonance: dossier?.dspDefaults.chestResonance ?? preset.chestResonance,
      commsGrit: dossier?.dspDefaults.commsGrit ?? preset.commsGrit,
      helmetBandwidth: dossier?.dspDefaults.helmetBandwidth ?? preset.helmetBandwidth,
      cabinReverb: dossier?.dspDefaults.cabinReverb ?? preset.cabinReverb,
      squelchClicks: dossier?.dspDefaults.squelchClicks ?? preset.squelchClicks,
      masteringProfile: dossier?.dspDefaults.masteringProfile ?? config.masteringProfile ?? 'studio_master',
      narratorPitch: preset.voicePitch,
      narratorPace: preset.voicePace,
    };
    setConfig(updated);
    saveAIConfig(updated);
    onConfigChange?.(updated);
  };

  const handleSliderChange = (field: keyof GoogleGenAIConfig, value: any) => {
    const updated = { ...config, [field]: value };
    setConfig(updated);
    saveAIConfig(updated);
    onConfigChange?.(updated);
  };

  const handleResetDefaults = () => {
    const defaultPreset = CHARACTER_VOICE_PRESETS.marcus_ward;
    handlePresetSelect(defaultPreset);
  };

  const handleTestVoice = async () => {
    const controller = getNarrationController();
    if (isTesting) {
      controller.stop();
      setIsTesting(false);
      return;
    }

    setIsTesting(true);
    try {
      await controller.narratePage([testText], config, selectedPresetId);
    } finally {
      setIsTesting(false);
    }
  };

  // Convert dB to VU meter bar width percentage
  const dbToPercent = (db: number) => {
    const clamped = Math.max(-40, Math.min(3, db));
    return ((clamped + 40) / 43) * 100;
  };

  return (
    <div className="voice-studio-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="voice-studio-modal-card hardware-console-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '980px', width: '95vw', maxHeight: '92vh', overflowY: 'auto' }}
      >
        {/* Hardware Console Chassis Header */}
        <div className="hardware-chassis-header">
          <div className="rack-mount-screw top-left" />
          <div className="rack-mount-screw top-right" />
          <div className="header-badge-group">
            <Activity size={20} className="header-mic-icon" style={{ color: '#00f3ff' }} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <h2 className="voice-studio-title" style={{ letterSpacing: '0.12em' }}>
                  GERYON AUDIO // HOLLYWOOD CINEMA MASTERING CONSOLE
                </h2>
                <span className="rack-unit-badge">2U RACK UNIT // DUAL-DSP</span>
              </div>
              <p className="voice-studio-subtitle">
                Hardware-Grade Tube Saturation, 4-Pole Vocal EQ, Subharmonic Fundamental & Real-Time A/B Cinema Engine
              </p>
            </div>
          </div>
          <button onClick={onClose} className="voice-studio-close-btn" aria-label="Close Voice Studio">
            <X size={18} />
          </button>
        </div>

        {/* =========================================================================
            SECTION 1: HARDWARE MASTERING RACK & LIVE CANVAS VISUALIZER (SPECTROGRAM / OSCILLOSCOPE)
            ========================================================================= */}
        <div className="hardware-dsp-rack-panel">
          {/* Top Rack Row: A/B Compare Switch + Visualizer Controls + Stereo VU Meters */}
          <div className="rack-top-controls-bar">
            {/* The Legendary Night-and-Day A/B Compare Toggle */}
            <div className="ab-compare-container">
              <span className="ab-section-title">A/B ACOUSTIC COMPARISON:</span>
              <button
                type="button"
                onClick={toggleABCompare}
                className={`ab-toggle-switch ${isDSPMastered ? 'ab-active-master' : 'ab-active-raw'}`}
                title="Click to toggle in real-time between Cinema Mastered DSP and Raw Unprocessed Audio"
              >
                <div className={`ab-pill ${isDSPMastered ? 'pill-master' : 'pill-raw'}`}>
                  {isDSPMastered ? (
                    <>
                      <Zap size={13} fill="currentColor" />
                      <span>PRO CINEMA MASTER (TUBE + 4-POLE EQ + REVERB)</span>
                    </>
                  ) : (
                    <>
                      <Volume2 size={13} />
                      <span>RAW VOICE DIRECT (FLAT / UNPROCESSED)</span>
                    </>
                  )}
                </div>
              </button>
            </div>

            {/* Visualizer Mode Toggle */}
            <div className="visualizer-mode-toggle-group">
              <span className="mode-toggle-label">ANALYZER:</span>
              <button
                type="button"
                onClick={() => setVisualizerMode('spectrogram')}
                className={`mode-btn ${visualizerMode === 'spectrogram' ? 'mode-btn-active' : ''}`}
              >
                FFT SPECTROGRAM
              </button>
              <button
                type="button"
                onClick={() => setVisualizerMode('oscilloscope')}
                className={`mode-btn ${visualizerMode === 'oscilloscope' ? 'mode-btn-active' : ''}`}
              >
                OSCILLOSCOPE
              </button>
            </div>

            {/* Stereo Hardware VU Meters */}
            <div className="hardware-vu-meters-block">
              <div className="vu-meter-row">
                <span className="vu-ch-label">L</span>
                <div className="vu-meter-track">
                  <div
                    className="vu-meter-bar"
                    style={{
                      width: `${dbToPercent(vuLeft)}%`,
                      background:
                        vuLeft > -3
                          ? 'linear-gradient(90deg, #10b981 60%, #f59e0b 80%, #ef4444 100%)'
                          : 'linear-gradient(90deg, #10b981 70%, #f59e0b 100%)',
                    }}
                  />
                  <div
                    className="vu-peak-needle"
                    style={{ left: `${dbToPercent(peakHoldLeft)}%` }}
                  />
                </div>
              </div>
              <div className="vu-meter-row">
                <span className="vu-ch-label">R</span>
                <div className="vu-meter-track">
                  <div
                    className="vu-meter-bar"
                    style={{
                      width: `${dbToPercent(vuRight)}%`,
                      background:
                        vuRight > -3
                          ? 'linear-gradient(90deg, #10b981 60%, #f59e0b 80%, #ef4444 100%)'
                          : 'linear-gradient(90deg, #10b981 70%, #f59e0b 100%)',
                    }}
                  />
                </div>
              </div>
              <div className={`vu-clip-led ${isClipping ? 'clip-active' : ''}`} title="Signal Peak Overload">
                CLIP
              </div>
            </div>
          </div>

          {/* Real-Time Visualizer Canvas */}
          <div className="canvas-wrapper-chassis">
            <canvas
              ref={canvasRef}
              width={880}
              height={140}
              className="dsp-visualizer-canvas"
            />
            <div className="canvas-overlay-status">
              <span className="status-tag">
                ACTIVE BUS: {isDSPMastered ? 'HOLLYWOOD CINEMA DSP [MASTERED]' : 'RAW DIRECT [BYPASS]'}
              </span>
              <span className="status-tag">
                PROFILE: {config.masteringProfile === 'comm_link' ? 'EVA COMM-LINK' : 'BROADCAST STUDIO'}
              </span>
              {playingFile && (
                <span className="status-tag playing-file-tag">
                  PLAYING: {playingFile} ({Math.floor(sampleCurrentTime)}s / {Math.floor(sampleDuration)}s)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* =========================================================================
            SECTION 2: MULTI-ENGINE ARCHITECTURE TABS
            ========================================================================= */}
        <div className="hardware-engine-tabs-bar">
          <button
            type="button"
            onClick={() => setActiveTab('dsp-master')}
            className={`engine-tab-btn ${activeTab === 'dsp-master' ? 'engine-tab-active' : ''}`}
          >
            <Sliders size={14} />
            <span>CINEMA MASTER DSP</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('dossier-inspector')}
            className={`engine-tab-btn ${activeTab === 'dossier-inspector' ? 'engine-tab-active' : ''}`}
          >
            <Headphones size={14} />
            <span>CHARACTER ACOUSTIC DOSSIER</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('gemini-live');
              handleSliderChange('voiceModel', 'gemini-2.0-flash');
            }}
            className={`engine-tab-btn ${activeTab === 'gemini-live' ? 'engine-tab-active' : ''}`}
          >
            <Zap size={14} />
            <span>GOOGLE GEMINI 2.0 FLASH AUDIO</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('kokoro');
              handleSliderChange('voiceModel', 'kokoro-82m');
            }}
            className={`engine-tab-btn ${activeTab === 'kokoro' ? 'engine-tab-active' : ''}`}
          >
            <Flame size={14} />
            <span>KOKORO-82M (FREE HOSTED)</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('elevenlabs');
              handleSliderChange('voiceModel', 'elevenlabs');
            }}
            className={`engine-tab-btn ${activeTab === 'elevenlabs' ? 'engine-tab-active' : ''}`}
          >
            <Sparkles size={14} />
            <span>ELEVENLABS STUDIO</span>
          </button>
        </div>

        {/* =========================================================================
            SECTION 3: CHARACTER ROSTER PRESETS (ALL 11 CHARACTERS)
            ========================================================================= */}
        <div className="voice-studio-presets-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
            <span className="section-label">
              <Radio size={13} />
              <span>ENSEMBLE CHARACTER ROSTER (SELECT TO CALIBRATE ACOUSTICS):</span>
            </span>
            <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.65rem', color: '#00f3ff' }}>
              11 CAST MEMBERS // 32 MULTI-STATE PERFORMANCES
            </span>
          </div>

          <div className="presets-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
            {Object.values(CHARACTER_VOICE_PRESETS).map((p) => {
              const isActive = selectedPresetId === p.id;
              const dossier = CHARACTER_ACOUSTIC_DOSSIERS[p.id];
              const states = dossier?.states || ELEVENLABS_CHARACTER_STATES[p.id] || [];
              const defaultSample = states[0];
              const isSamplePlaying = defaultSample && playingFile === defaultSample.targetFile;

              return (
                <button
                  key={p.id}
                  onClick={() => handlePresetSelect(p)}
                  className={`preset-card-btn ${isActive ? 'preset-active' : ''}`}
                >
                  <div className="preset-card-top">
                    <div className="preset-name-gender-row">
                      <span className="preset-name">{p.name}</span>
                      <span className={`preset-gender-badge gender-${p.gender}`}>
                        {p.gender === 'male' ? 'MALE' : p.gender === 'female' ? 'FEMALE' : 'ENTITY'}
                      </span>
                    </div>
                    {isActive && <Check size={13} className="preset-check" />}
                  </div>

                  <span className="preset-role" style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                    {p.characterTitle}
                  </span>

                  {dossier && (
                    <div style={{ marginTop: '0.25rem', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.62rem', color: '#38bdf8' }}>
                      f₀: {dossier.fundamentalHz.typical} Hz ({dossier.fundamentalHz.min}–{dossier.fundamentalHz.max} Hz)
                    </div>
                  )}

                  <div className="preset-voice-models" style={{ marginTop: '0.35rem' }}>
                    <span className="preset-gemini-tag">GEMINI: {p.geminiVoice || 'Fenrir'}</span>
                    <span className="preset-neural-tag">KOKORO: {dossier?.engineMappings.kokoroVoice || 'am_adam'}</span>
                  </div>

                  {defaultSample && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '0.4rem',
                        paddingTop: '0.3rem',
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.6rem', color: '#64748b' }}>
                        {states.length} STATES
                      </span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePresetSelect(p);
                          playSampleAudio(defaultSample.targetFile);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          background: isSamplePlaying ? '#00f3ff' : 'rgba(0, 243, 255, 0.16)',
                          color: isSamplePlaying ? '#030508' : '#00f3ff',
                          border: '1px solid rgba(0, 243, 255, 0.4)',
                          borderRadius: '3px',
                          padding: '0.15rem 0.5rem',
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                        title={`Audition mastered voice sample for ${p.name}`}
                      >
                        {isSamplePlaying ? <Square size={9} fill="currentColor" /> : <Play size={9} fill="currentColor" />}
                        <span>{isSamplePlaying ? 'STOP' : 'AUDITION'}</span>
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* =========================================================================
            SECTION 4: ACTIVE TAB VIEWPORT
            ========================================================================= */}
        {activeTab === 'dossier-inspector' && currentDossier && (
          <div className="dossier-inspector-panel">
            <div className="dossier-hero-row">
              <div className="dossier-avatar-badge">
                <span className="avatar-f0-hz">{currentDossier.fundamentalHz.typical} Hz</span>
                <span className="avatar-role-tag">{currentDossier.fundamentalHz.vocalCategory}</span>
              </div>
              <div className="dossier-hero-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <h3 className="dossier-character-name">{currentDossier.name}</h3>
                  <span className="dossier-archetype-pill">
                    ARCHETYPE: {currentDossier.referenceArchetype.primaryActor} ({currentDossier.referenceArchetype.movieOrRole})
                  </span>
                </div>
                <p className="dossier-character-title">{currentDossier.characterTitle}</p>
                <p className="dossier-texture-summary">{currentDossier.referenceArchetype.vocalTextureSummary}</p>
              </div>
            </div>

            <div className="dossier-grid-two-col">
              <div className="dossier-detail-card">
                <h4 className="dossier-card-title">RESPIRATION & CADENCE ARCHITECTURE</h4>
                <div className="dossier-field">
                  <span className="field-label">BREATHING PATTERN:</span>
                  <p className="field-value">{currentDossier.respirationAndCadence.breathingPattern}</p>
                </div>
                <div className="dossier-field">
                  <span className="field-label">SPEECH CADENCE:</span>
                  <p className="field-value">{currentDossier.respirationAndCadence.speechCadence}</p>
                </div>
                <div className="dossier-field">
                  <span className="field-label">MICRO-RESPIRATION TOKENS:</span>
                  <div className="tokens-row">
                    {currentDossier.respirationAndCadence.microArtifacts.map((tok, i) => (
                      <span key={i} className="respiration-token">{tok}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dossier-detail-card">
                <h4 className="dossier-card-title">DSP CHANNEL STRIP & MODEL MAPPINGS</h4>
                <div className="dossier-field">
                  <span className="field-label">ACOUSTIC PROFILE:</span>
                  <p className="field-value">{currentDossier.acousticNotes}</p>
                </div>
                <div className="dossier-chips-row">
                  <span className="dsp-chip">GEMINI 2.0: {currentDossier.engineMappings.gemini2FlashVoice}</span>
                  <span className="dsp-chip">KOKORO: {currentDossier.engineMappings.kokoroVoice}</span>
                  <span className="dsp-chip">CLOUD JOURNEY: {currentDossier.engineMappings.googleCloudVoice}</span>
                  <span className="dsp-chip">ELEVEN: {currentDossier.states[0]?.elevenLabsVoiceName || 'Adam'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dramatic Performance States Row */}
        {(() => {
          const currentStates = currentDossier?.states || ELEVENLABS_CHARACTER_STATES[selectedPresetId] || [];
          if (currentStates.length === 0) return null;
          const activeState = (config.characterStateId && currentStates.find((s) => s.stateId === config.characterStateId)) || currentStates[0];

          return (
            <div className="voice-studio-states-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <span className="section-label" style={{ marginBottom: 0 }}>
                  <Sparkles size={13} style={{ color: '#fbbf24' }} />
                  <span>DRAMATIC PERFORMANCE STATES FOR {currentDossier?.name.toUpperCase() || 'CHARACTER'}:</span>
                </span>
                <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.65rem', color: '#00f3ff' }}>
                  ★ 100% CINEMA MASTER RECORDINGS WITH DSP BUS ROUTING
                </span>
              </div>

              <div className="state-pills-row">
                {currentStates.map((st) => {
                  const isStateActive = activeState?.stateId === st.stateId;
                  const isThisPlaying = playingFile === st.targetFile;
                  return (
                    <div
                      key={st.stateId}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'stretch',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        border: isStateActive ? '1px solid #00f3ff' : '1px solid rgba(255, 255, 255, 0.12)',
                        background: isStateActive ? 'rgba(0, 243, 255, 0.15)' : 'rgba(15, 23, 42, 0.8)',
                        boxShadow: isStateActive ? '0 0 10px rgba(0, 243, 255, 0.25)' : 'none',
                      }}
                    >
                      <button
                        onClick={() => {
                          const updated: GoogleGenAIConfig = {
                            ...config,
                            characterStateId: st.stateId,
                          };
                          setConfig(updated);
                          saveAIConfig(updated);
                          onConfigChange?.(updated);
                          setTestText(st.script);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: isStateActive ? '#ffffff' : '#cbd5e1',
                          padding: '0.4rem 0.65rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          fontFamily: 'var(--font-mono, monospace)',
                          fontSize: '0.72rem',
                          textAlign: 'left',
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{st.stateName}</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          playSampleAudio(st.targetFile);
                        }}
                        style={{
                          background: isThisPlaying ? '#00f3ff' : 'rgba(0, 243, 255, 0.15)',
                          border: 'none',
                          borderLeft: '1px solid rgba(0, 243, 255, 0.3)',
                          color: isThisPlaying ? '#030508' : '#00f3ff',
                          padding: '0 0.55rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        title={isThisPlaying ? 'Stop playback' : `Play ${st.targetFile}`}
                        aria-label={`Audition sample ${st.targetFile}`}
                      >
                        {isThisPlaying ? <Square size={11} fill="currentColor" /> : <Play size={11} fill="currentColor" />}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Active State Featured Spotlight Card */}
              {activeState && (
                <div
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.85rem 1.1rem',
                    background: 'rgba(3, 8, 16, 0.95)',
                    border: '1px solid rgba(0, 243, 255, 0.35)',
                    borderRadius: '5px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Volume2 size={16} color="#00f3ff" />
                      <div>
                        <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.62rem', color: '#38bdf8', letterSpacing: '0.08em' }}>
                          DIRECT CINEMA DSP MASTER AUDITION // ZERO API KEY REQUIRED
                        </span>
                        <h4 style={{ margin: 0, color: '#ffffff', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.85rem' }}>
                          {currentDossier?.name.toUpperCase()} — {activeState.stateName.toUpperCase()}
                        </h4>
                      </div>
                    </div>
                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.62rem', color: '#ffd700', background: 'rgba(255, 215, 0, 0.12)', border: '1px solid rgba(255, 215, 0, 0.3)', padding: '0.15rem 0.45rem', borderRadius: '2px' }}>
                      {activeState.targetFile}
                    </span>
                  </div>

                  <p style={{ margin: '0.5rem 0', fontStyle: 'italic', color: '#cbd5e1', fontSize: '0.8rem', lineHeight: '1.45', borderLeft: '2px solid #00f3ff', paddingLeft: '0.7rem' }}>
                    "{activeState.script}"
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => playSampleAudio(activeState.targetFile)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.45rem',
                        padding: '0.45rem 1rem',
                        background: playingFile === activeState.targetFile ? 'linear-gradient(135deg, #ef4444, #b91c1c)' : 'linear-gradient(135deg, #00f3ff, #0284c7)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        fontFamily: 'var(--font-mono, monospace)',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: playingFile === activeState.targetFile ? '0 0 12px rgba(239, 68, 68, 0.4)' : '0 0 12px rgba(0, 243, 255, 0.4)',
                      }}
                    >
                      {playingFile === activeState.targetFile ? (
                        <>
                          <Square size={13} fill="currentColor" />
                          <span>STOP MASTER RECORDING</span>
                        </>
                      ) : (
                        <>
                          <Play size={13} fill="currentColor" />
                          <span>PLAY AUTHENTIC MASTER RECORDING (ROUTED TO DSP)</span>
                        </>
                      )}
                    </button>

                    <div style={{ flex: 1, minWidth: '140px', height: '6px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: playingFile === activeState.targetFile ? `${sampleProgress}%` : '0%',
                          background: 'linear-gradient(90deg, #00f3ff, #ffd700)',
                          transition: 'width 0.1s linear',
                        }}
                      />
                    </div>

                    <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.68rem', color: '#94a3b8' }}>
                      {playingFile === activeState.targetFile
                        ? `${Math.floor(sampleCurrentTime)}s / ${Math.floor(sampleDuration)}s`
                        : `${currentDossier?.referenceArchetype.primaryActor.split(' ')[0]} // Cinema DSP`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* =========================================================================
            SECTION 5: REAL-TIME DSP HARDWARE TUNING SLIDERS
            ========================================================================= */}
        <div className="voice-studio-controls-grid">
          {/* Pitch Transposition */}
          <div className="tuner-control-box">
            <div className="tuner-label-row">
              <span className="tuner-name">VOCAL PITCH TRANSPOSITION</span>
              <span className="tuner-val">
                {config.voicePitch.toFixed(2)}x (
                {config.voicePitch < 0.85
                  ? 'DEEP BARITONE'
                  : config.voicePitch > 1.05
                  ? 'ALTO / SHARP'
                  : 'MID-RANGE'}
                )
              </span>
            </div>
            <input
              type="range"
              min="0.60"
              max="1.30"
              step="0.02"
              value={config.voicePitch}
              onChange={(e) => handleSliderChange('voicePitch', parseFloat(e.target.value))}
              className="tuner-slider"
            />
            <span className="tuner-hint">Drops voice pitch for weary, heavy, older vocal timbre</span>
          </div>

          {/* Speaking Pace */}
          <div className="tuner-control-box">
            <div className="tuner-label-row">
              <span className="tuner-name">SPEAKING PACE & CADENCE</span>
              <span className="tuner-val">{config.voicePace.toFixed(2)}x</span>
            </div>
            <input
              type="range"
              min="0.65"
              max="1.35"
              step="0.03"
              value={config.voicePace}
              onChange={(e) => handleSliderChange('voicePace', parseFloat(e.target.value))}
              className="tuner-slider"
            />
            <span className="tuner-hint">Controls narrative rhythm from deliberate to hurried</span>
          </div>

          {/* Chest Sub-Bass Resonance */}
          <div className="tuner-control-box">
            <div className="tuner-label-row">
              <span className="tuner-name">CHEST SUB-BASS RESONANCE (110Hz LOW-SHELF)</span>
              <span className="tuner-val">{Math.round(config.chestResonance * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.0"
              max="1.0"
              step="0.05"
              value={config.chestResonance}
              onChange={(e) => handleSliderChange('chestResonance', parseFloat(e.target.value))}
              className="tuner-slider"
            />
            <span className="tuner-hint">Boosts 95-130Hz vocal fundament for bone-density presence</span>
          </div>

          {/* Comms Tube Grit / Saturation */}
          <div className="tuner-control-box">
            <div className="tuner-label-row">
              <span className="tuner-name">ANALOG TUBE DRIVE / SATURATION</span>
              <span className="tuner-val">{Math.round(config.commsGrit * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.0"
              max="1.0"
              step="0.05"
              value={config.commsGrit}
              onChange={(e) => handleSliderChange('commsGrit', parseFloat(e.target.value))}
              className="tuner-slider"
            />
            <span className="tuner-hint">Non-linear WaveShaper curve for warm analog overdrive</span>
          </div>

          {/* Starship Bulkhead Reverb */}
          <div className="tuner-control-box">
            <div className="tuner-label-row">
              <span className="tuner-name">STARSHIP BULKHEAD REVERB & DELAY</span>
              <span className="tuner-val">{Math.round(config.cabinReverb * 100)}%</span>
            </div>
            <input
              type="range"
              min="0.0"
              max="1.0"
              step="0.05"
              value={config.cabinReverb}
              onChange={(e) => handleSliderChange('cabinReverb', parseFloat(e.target.value))}
              className="tuner-slider"
            />
            <span className="tuner-hint">Early 38ms stereo reflections of a titanium corridor</span>
          </div>

          {/* Helmet Radio Bandwidth */}
          <div className="tuner-control-box">
            <div className="tuner-label-row">
              <span className="tuner-name">HELMET RADIO BANDWIDTH (PRESENCE)</span>
              <span className="tuner-val">{config.helmetBandwidth} Hz</span>
            </div>
            <input
              type="range"
              min="900"
              max="4800"
              step="50"
              value={config.helmetBandwidth}
              onChange={(e) => handleSliderChange('helmetBandwidth', parseInt(e.target.value, 10))}
              className="tuner-slider"
            />
            <span className="tuner-hint">High-frequency cutoff and bandpass curve</span>
          </div>
        </div>

        {/* Mastering Profile & Toggles */}
        <div style={{ marginTop: '0.8rem', padding: '0.75rem', background: 'rgba(5, 12, 22, 0.7)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="system-voice-label-row">
            <span className="system-voice-label">
              <Settings size={13} />
              <span>ACOUSTIC MASTERING ARCHITECTURE & DSP PROFILE:</span>
            </span>
            <span className="system-voice-count">
              {config.masteringProfile === 'comm_link'
                ? 'COMM-LINK TRANSCEIVER (EVA SUIT)'
                : 'STUDIO MASTER BROADCAST'}
            </span>
          </div>
          <select
            value={config.masteringProfile || 'studio_master'}
            onChange={(e) => handleSliderChange('masteringProfile', e.target.value as any)}
            className="voice-studio-select"
          >
            <option value="studio_master">
              ★ STUDIO MASTER (BROADCAST FIDELITY) — [WARMTH 130Hz, CRISP PRESENCE 3.2kHz, BROADCAST COMPRESSOR, 0% SQUELCH/MUFFLE]
            </option>
            <option value="comm_link">
              EVA COMM-LINK TRANSCEIVER — [TACTICAL HELMET RADIO WITH ANALOG GRIT & BANDPASS SQUELCH]
            </option>
          </select>
        </div>

        {/* Dynamic Multi-Voice Ensemble Toggle */}
        <div className="voice-studio-toggle-row">
          <label className="toggle-label-group">
            <input
              type="checkbox"
              checked={config.multiVoiceNarration !== false}
              onChange={(e) => handleSliderChange('multiVoiceNarration', e.target.checked)}
              className="toggle-checkbox"
            />
            <span className="toggle-custom" />
            <div className="toggle-text-block">
              <span className="toggle-title">
                <Users size={12} style={{ display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                DYNAMIC MULTI-SPEAKER ENSEMBLE (MALE / FEMALE DIALOGUE SWITCHING)
              </span>
              <span className="toggle-desc">
                Automatically switches distinct character voices: Marcus (Gravelly Male), Elena (Chilling Female), Thorne (Subterranean Bass), Silas (Tense Male), Vera (Choral Organ)
              </span>
            </div>
          </label>
        </div>

        {/* Toggle Squelch Clicks */}
        <div className="voice-studio-toggle-row">
          <label className="toggle-label-group">
            <input
              type="checkbox"
              checked={config.squelchClicks}
              onChange={(e) => handleSliderChange('squelchClicks', e.target.checked)}
              className="toggle-checkbox"
            />
            <span className="toggle-custom" />
            <div className="toggle-text-block">
              <span className="toggle-title">PROCEDURAL COMMS SQUELCH / PTT CLICKS</span>
              <span className="toggle-desc">
                Plays metallic walkie-talkie mic transient clicks at start and end of utterances
              </span>
            </div>
          </label>
        </div>

        {/* Synchronized Per-Word Text Highlighter Toggle */}
        <div className="voice-studio-toggle-row">
          <label className="toggle-label-group">
            <input
              type="checkbox"
              checked={config.enableReadAlongHighlight !== false}
              onChange={(e) => handleSliderChange('enableReadAlongHighlight', e.target.checked)}
              className="toggle-checkbox"
            />
            <span className="toggle-custom" />
            <div className="toggle-text-block">
              <span className="toggle-title">
                <Highlighter size={12} style={{ display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                SYNCHRONIZED PER-WORD TEXT HIGHLIGHTER (REAL-TIME LUMINESCENT TRACKING)
              </span>
              <span className="toggle-desc">
                Dynamically illuminates and tracks each spoken word across the page in real-time as speech progresses, with smooth luminescent aura and click-to-speak word jumps.
              </span>
            </div>
          </label>
        </div>

        {/* Cloud Voice Authentication Panels when relevant */}
        {config.voiceModel === 'gemini-2.0-flash' && (
          <div className="gemini-auth-box">
            <div className="gemini-auth-header">
              <span className="gemini-auth-title">GOOGLE GEMINI 2.0 FLASH AUDIO AUTHENTICATION</span>
              <a
                href="https://aistudio.google.com/app/apikey"
                target="_blank"
                rel="noreferrer"
                className="gemini-key-link"
              >
                Get Free Gemini API Key ↗
              </a>
            </div>
            <input
              type="password"
              placeholder="Paste Google AI Studio API Key (AIzaSy...) or OAuth Bearer (ya29...)"
              value={config.apiKey || config.oauthToken || ''}
              onChange={(e) => {
                const val = e.target.value.trim();
                if (val.startsWith('ya29.')) {
                  handleSliderChange('oauthToken', val);
                } else {
                  handleSliderChange('apiKey', val);
                }
              }}
              className="gemini-api-input"
            />
            <span className="gemini-auth-hint">
              Streams native 24kHz Google DeepMind audio: Fenrir (Marcus/Narrator), Aoede (Elena/Vera), Charon (Thorne/Spectator), Puck (Silas/Benny).
            </span>
          </div>
        )}

        {config.voiceModel === 'elevenlabs' && (
          <div className="elevenlabs-auth-box">
            <div className="elevenlabs-auth-header">
              <span className="elevenlabs-auth-title">
                <Key size={12} />
                ELEVENLABS CINEMATIC VOICE API CONFIGURATION
              </span>
              <a
                href="https://elevenlabs.io/app/voice-library"
                target="_blank"
                rel="noreferrer"
                className="elevenlabs-key-link"
              >
                Get ElevenLabs API Key ↗
              </a>
            </div>
            <input
              type="password"
              placeholder="Paste ElevenLabs API Key (xi-api-key / sk_...)"
              value={config.elevenLabsApiKey || ''}
              onChange={(e) => handleSliderChange('elevenLabsApiKey', e.target.value.trim())}
              className="elevenlabs-api-input"
            />
            <span className="elevenlabs-auth-hint">
              Streams hyper-realistic biological speech with authentic breath tokens, grit, and emotional stability curves across all characters.
            </span>
          </div>
        )}

        {/* Ensemble Jukebox Expander */}
        <div style={{ marginTop: '0.8rem' }}>
          <button
            type="button"
            onClick={() => setIsJukeboxOpen(!isJukeboxOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'rgba(12, 20, 34, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              padding: '0.45rem 0.8rem',
              color: '#ffd700',
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '0.72rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <Music size={13} color="#ffd700" />
              <span>BROWSE & AUDITION ALL 32 CHARACTER SAMPLES ACROSS ENTIRE ENSEMBLE</span>
            </div>
            {isJukeboxOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {isJukeboxOpen && (
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.8rem',
                background: 'rgba(4, 8, 16, 0.95)',
                border: '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '4px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: '0.75rem',
                maxHeight: '260px',
                overflowY: 'auto',
              }}
            >
              {Object.entries(ELEVENLABS_CHARACTER_STATES).map(([charKey, states]) => {
                const charName = CHARACTER_VOICE_PRESETS[charKey]?.name || charKey.toUpperCase();
                return (
                  <div
                    key={charKey}
                    style={{
                      background: 'rgba(10, 16, 26, 0.8)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '4px',
                      padding: '0.5rem 0.65rem',
                    }}
                  >
                    <div style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem', color: '#00f3ff', fontWeight: 700, marginBottom: '0.35rem' }}>
                      {charName}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {states.map((s) => {
                        const isPlayingThis = playingFile === s.targetFile;
                        return (
                          <div
                            key={s.stateId}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '0.4rem',
                              fontSize: '0.68rem',
                              fontFamily: 'var(--font-mono, monospace)',
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => playSampleAudio(s.targetFile)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.3rem',
                                background: isPlayingThis ? '#00f3ff' : 'rgba(255, 255, 255, 0.06)',
                                color: isPlayingThis ? '#030508' : '#e2e8f0',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '3px',
                                padding: '0.2rem 0.45rem',
                                cursor: 'pointer',
                                textAlign: 'left',
                                flex: 1,
                              }}
                            >
                              {isPlayingThis ? <Square size={9} fill="currentColor" /> : <Play size={9} fill="currentColor" />}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.stateName}</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* =========================================================================
            SECTION 6: AUDITION BAR
            ========================================================================= */}
        <div className="voice-studio-audition-bar" style={{ marginTop: '1rem' }}>
          <div className="audition-input-group">
            <span className="audition-label">AUDITION PHRASE:</span>
            <input
              type="text"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              className="audition-input"
            />
          </div>

          <div className="audition-buttons-row">
            {/* Direct Pre-Rendered High-Fidelity MP3 Sample Audition */}
            {(() => {
              const currentStates = currentDossier?.states || ELEVENLABS_CHARACTER_STATES[selectedPresetId] || [];
              const activeState = (config.characterStateId && currentStates.find((s) => s.stateId === config.characterStateId)) || currentStates[0];
              if (!activeState?.targetFile) return null;
              const isPlayingThis = playingFile === activeState.targetFile;
              return (
                <button
                  type="button"
                  onClick={() => playSampleAudio(activeState.targetFile)}
                  className={`audition-play-btn ${isPlayingThis ? 'testing-active' : ''}`}
                  style={{
                    background: isPlayingThis ? '#ef4444' : 'linear-gradient(135deg, #00f3ff, #0284c7)',
                    borderColor: isPlayingThis ? '#ef4444' : '#00f3ff',
                    color: '#ffffff',
                    fontWeight: 700,
                  }}
                  title={`Play authentic pre-rendered ${activeState.targetFile} (Zero API key)`}
                >
                  {isPlayingThis ? <Square size={15} fill="currentColor" /> : <Volume2 size={15} />}
                  <span>{isPlayingThis ? 'STOP MASTER' : 'AUDITION MASTER (ROUTED TO DSP)'}</span>
                </button>
              );
            })()}

            <button
              onClick={handleTestVoice}
              className={`audition-play-btn ${isTesting ? 'testing-active' : ''}`}
            >
              {isTesting ? <Square size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" />}
              <span>{isTesting ? 'STOP TEST' : 'TEST LIVE VOICE'}</span>
            </button>

            <button onClick={handleResetDefaults} className="audition-reset-btn" title="Reset to Marcus Ward">
              <RotateCcw size={14} />
              <span>RESET DEFAULTS</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
